import { useEffect } from "react";
import type { RefObject } from "react";
import { track } from "../../lib/events";
import { activeMilliseconds, activeSecondsSince } from "../../lib/activeTime";
import type { ContentItem } from "../../types";

const THRESHOLDS = [25, 50, 75, 100];

// How far down a piece of text the teacher has scrolled, reported when each
// quarter first comes into view, with the active seconds spent by then and the
// word count. Opening an item says nothing about whether it was read; this
// gives the depth, and with the time it gives a reading speed to judge by.
// Text that fits on the screen reports every quarter at once, marked
// fits_on_screen, because there was nothing to scroll.
export function useReadDepth(
  target: RefObject<HTMLElement | null>,
  item: ContentItem,
  moduleID: string,
  ready: boolean
) {
  useEffect(() => {
    const element = target.current;
    if (!ready || !element) return undefined;
    const startedAt = activeMilliseconds();
    const reached = new Set<number>();
    const fits = element.getBoundingClientRect().height <= window.innerHeight;
    let frame = 0;

    const check = () => {
      frame = 0;
      const rect = element.getBoundingClientRect();
      if (rect.height <= 0) return;
      const depth = Math.min(1, Math.max(0, (window.innerHeight - rect.top) / rect.height)) * 100;
      for (const threshold of THRESHOLDS) {
        if (depth >= threshold && !reached.has(threshold)) {
          reached.add(threshold);
          track("text_read_progress", {
            module_id: moduleID,
            item_id: item.id,
            payload: {
              percent: threshold,
              active_seconds: activeSecondsSince(startedAt),
              words: (element.textContent || "").trim().split(/\s+/).filter(Boolean).length,
              fits_on_screen: fits,
            },
          });
        }
      }
    };
    const schedule = () => {
      if (!frame) frame = window.requestAnimationFrame(check);
    };
    // A moment for images and fonts to settle before the first look; a timer
    // rather than an animation frame, which a hidden page never runs.
    const first = window.setTimeout(check, 100);
    window.addEventListener("scroll", schedule, { passive: true });
    window.addEventListener("resize", schedule);
    return () => {
      window.removeEventListener("scroll", schedule);
      window.removeEventListener("resize", schedule);
      window.clearTimeout(first);
      if (frame) window.cancelAnimationFrame(frame);
    };
  }, [target, item.id, moduleID, ready]);
}
